import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import {
  Grapheme,
  ScopeAndSequence,
  Word,
} from '@/common/types/scopeAndSequence';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import StudentGame from '@/game/GameComponents/PhaserGame/StudentGames/StudentGame';
import {
  isPhaserDevMode,
  waitForMilliseconds,
  waitForSeconds,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import _ from 'lodash';

export function getBackingFromWidth(width: number, isLocked: boolean) {
  if (width <= 48) {
    return isLocked ? 'CardLocked_48' : 'Card_48';
  } else if (width <= 64) {
    return isLocked ? 'CardLocked_64' : 'Card_64';
  } else if (width <= 96) {
    return isLocked ? 'CardLocked_96' : 'Card_96';
  } else if (width <= 128) {
    return isLocked ? 'CardLocked_128' : 'Card_128';
  } else if (width <= 192) {
    return isLocked ? 'CardLocked_192' : 'Card_192';
  } else if (width <= 256) {
    return isLocked ? 'CardLocked_256' : 'Card_256';
  }
  return isLocked ? 'CardLocked-512' : 'Card-512';
}

export function getOutlineFromWidth(width: number) {
  if (width <= 48) {
    return 'Card-Highlight-48';
  } else if (width <= 64) {
    return 'Card-Highlight-64';
  } else if (width <= 96) {
    return 'Card-Highlight-96';
  } else if (width <= 128) {
    return 'Card-Highlight-128';
  } else if (width <= 192) {
    return 'Card-Highlight-192';
  } else if (width <= 256) {
    return 'Card-Highlight-256';
  }
  return 'Card-Highlight-256';
}

export async function playAIWordWithCards(
  game: StudentGame,
  wordGraphemes: Grapheme[],
  aiSpeedMod: number,
  targetWord: Word,
  onComplete: (attempt: string) => void,
) {
  let aiAttempt = '';

  // Function to select cards with a delay
  const selectCardWithDelay = async (index: number) => {
    if (index < wordGraphemes.length - 1) {
      const grapheme = wordGraphemes[index].spelling;
      const cardIndex = game
        .getHandCards()
        .findIndex((card) => card.cardText === grapheme);

      if (cardIndex !== -1) {
        // Fake clicking on the card.
        game.manuallySelectCard(
          game.getHandCards()[cardIndex] as StudentGameCard,
        );
        aiAttempt += grapheme;
      }
      // Call the function recursively with a delay
      await waitForMilliseconds(game.time, 500 * aiSpeedMod);
      selectCardWithDelay(index + 1);
    } else if (index === wordGraphemes.length - 1) {
      // Introduce a chance to select the wrong card.
      // How likely the AI is to mess up the word.
      const failureChance = getAIFailureChance();
      if (Math.random() <= failureChance) {
        const firstInvalidCard: StudentGameCard | undefined = game
          .getHandCards()
          .find((c) => {
            return (
              aiAttempt + (c as StudentGameCard)?.cardText !==
              targetWord.spelling
            );
          });
        if (firstInvalidCard) {
          // Fake clicking on the card.
          game.manuallySelectCard(firstInvalidCard);
          aiAttempt += firstInvalidCard?.cardText;
        } else {
          // If no invalid card is found, select the first card.
          game.manuallySelectCard(game.getHandCards()[0]);
          aiAttempt += game.getHandCards()[0]?.cardText;
        }
      } else {
        const grapheme = wordGraphemes[index].spelling;
        const cardIndex = game
          .getHandCards()
          .findIndex((card) => card.cardText === grapheme);
        if (cardIndex !== -1) {
          game.manuallySelectCard(game.getHandCards()[cardIndex]);
          aiAttempt += grapheme;
        }
      }

      await waitForMilliseconds(game.time, 500 * aiSpeedMod);
      await selectCardWithDelay(index + 1);
    } else {
      // Once all cards are selected, call the onComplete callback.
      await waitForMilliseconds(game.time, 750 * aiSpeedMod);
      onComplete(aiAttempt);
    }
  };
  selectCardWithDelay(0);
}

export async function playAIWordWithKeyboard(
  game: StudentGame,
  wordGraphemes: Grapheme[],
  aiSpeedMod: number,
  onComplete: (attempt: string) => void,
) {
  let aiAttempt = '';

  const letters = wordGraphemes.map((g) => g.spelling.split('')).flat();
  const failureChance = getAIFailureChance();
  for (var i = 0; i < letters.length; i++) {
    const correctLetter = letters[i][0];
    const randomIncorrectLetter = _.sample(
      (scopeAndSequence as ScopeAndSequence).graphemes
        .map((g) => g.spelling)
        .filter((l) => l.length === 1 && l !== correctLetter),
    );
    const nextLetter =
      i === letters.length - 1
        ? Math.random() <= failureChance
          ? randomIncorrectLetter
          : correctLetter
        : correctLetter;
    if (nextLetter) {
      await waitForSeconds(game.time, 0.5 * aiSpeedMod);
      aiAttempt += nextLetter;
      // Fake typing the letter
      game.inputHandler?.keyboardInputSystem?.handleKeyboardInput(
        {
          key: nextLetter,
          type: 'keydown',
        },
        true,
      );
    }
  }

  await waitForSeconds(game.time, 1 * aiSpeedMod);
  onComplete(aiAttempt);
}

export function getAIFailureChance() {
  return isPhaserDevMode(getStudent()) ? 0.5 : 0.5;
}
